import { ArrowRight, Eye, Lightbulb } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { player } from '../audio/player';
import type { RecordingResult } from '../audio/recorder';
import { canOfferRecording, micDeclined, noteMicProblem, undoMicDeclined, useMicAccess } from '../audio/useMic';
import { RoleChip } from '../components/Cast';
import { Illustration } from '../components/Illustration';
import { InlineGreek } from '../components/InlineGreek';
import { MicNote } from '../components/MicNote';
import { StepHead } from '../components/StepHead';
import { NoMicPanel } from '../components/NoMicPanel';
import { Compare } from '../components/Compare';
import { GreekText } from '../components/GreekText';
import { PlayButton } from '../components/PlayButton';
import { RecordControl, type MicProblem } from '../components/RecordControl';
import { frameOf, staffOf } from '../content';
import type { Item } from '../content/types';
import { clipFor, itemVoice, speechRange, VOICE_LABEL, voicesOf } from '../lib/voice';
import { introduce, useAppState } from '../state/store';
import { WhyNote } from './WhyNote';

interface Props {
  item: Item;
  onDone: () => void;
  /** A listening-only day: meet the phrase by ear, no speaking or recording. */
  quiet?: boolean;
}

type Phase = 'try' | 'listen' | 'say' | 'understood';

/**
 * Meeting a new phrase. Phrases on a known pattern start with a guess (trying first helps,
 * even when the guess is wrong). Then: listen, say it aloud while recording, compare with the
 * model, and only then the word-by-word and the "why" note.
 */
export function MeetStep({ item, onDone, quiet = false }: Props) {
  const { settings, profile } = useAppState();
  const [phase, setPhase] = useState<Phase>(item.tryFirst && !quiet ? 'try' : 'listen');
  const [recording, setRecording] = useState<RecordingResult | null>(null);
  const access = useMicAccess();
  const [refused, setRefused] = useState(micDeclined);
  const noMic = !canOfferRecording(access, refused);
  const retryMic = () => {
    undoMicDeclined();
    setRefused(false);
  };
  const onMicProblem = (problem: MicProblem) => {
    noteMicProblem(problem);
    setRefused(true);
  };
  const [listens, setListens] = useState(0);
  const [autoPlay, setAutoPlay] = useState(0);
  const [busy, setBusy] = useState(false);
  // Every recorded voice is a model for the match score; the phrase is heard in its speaker's voice.
  const voices = voicesOf(item);
  const voice = itemVoice(item, staffOf(item));
  const clip = clipFor(item, voice);
  const range = useMemo(() => speechRange(clip), [clip]);
  const frame = frameOf(item);
  // On a listening-only day every phrase is met as one you hear.
  const say = item.role === 'say' && !quiet;

  useEffect(() => () => {
    if (recording) URL.revokeObjectURL(recording.url);
  }, [recording]);

  useEffect(() => () => player.stop(), []);

  const keep = (r: RecordingResult) => {
    setRecording(r);
    setAutoPlay((n) => n + 1);
  };


  const onListened = (result: string) => {
    if (result !== 'ended') return;
    setListens((n) => n + 1);
    // The phase may have moved on while the clip played (Skip listening): only leave 'listen'.
    setPhase((p) => (p === 'listen' ? (say ? 'say' : 'understood') : p));
  };

  const next = async () => {
    if (busy) return;
    setBusy(true);
    player.stop();
    await introduce(item.id);
    onDone();
  };

  const showGreek = phase !== 'try';

  return (
    <div
      className="step meet"
      data-stage={phase === 'listen' && listens === 0 ? 'listen' : phase === 'try' ? 'try' : 'shown'}
      data-compared={showGreek && recording ? true : undefined}
    >
      <div className="stack">
        <StepHead
          kind={say ? 'say' : 'hear'}
          role={<RoleChip staff={staffOf(item)} from={item.role === 'say' ? 'you' : 'staff'} />}
          why={profile.lessonsDone.length === 0}
        >
          <span className="chip">New phrase</span>
          {quiet && <span className="chip chip-warn">Listening only</span>}
        </StepHead>
        {item.context && <p className="context">{item.context}</p>}
      </div>

      <Illustration id={item.id} size="phrase" priority className="meet-art" />

      {phase === 'try' ? (
        <div className="stack-lg reveal">
          <div className="stack">
            <p className="eyebrow">Try first</p>
            <h2 className="english-cue">{item.meaning}</h2>
          </div>
          {/* The hint gives most of it away, so it waits behind a tap: guess first. */}
          <details className="note">
            <summary className="note-title" style={{ cursor: 'pointer' }}>
              <Lightbulb size={14} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />
              Stuck? A hint
            </summary>
            <p style={{ marginTop: 6 }}>
              <InlineGreek text={item.tryFirst ?? ''} />
            </p>
          </details>
          <p className="muted">Have a go: say it aloud. Guessing helps you remember, even if the guess is wrong.</p>
          {noMic && <NoMicPanel title="Say your guess aloud" body="Then tap Show me to hear the model." access={access} onRetry={retryMic} />}
        </div>
      ) : phase === 'listen' && listens === 0 ? (
        // Audio first: the words appear once the learner has heard the phrase. The play button
        // waits in the thumb zone, below.
        <p className="muted small" style={{ textAlign: 'center' }}>
          The words appear after you've heard it.
        </p>
      ) : (
        <div className="stack-lg reveal">
          <div className="stack" style={{ gap: 6 }}>
            <GreekText text={item.text} size="hero" ipa={item.ipa} showRespelling={settings.showRespelling} />
            <p className="meaning">{item.meaning}</p>
          </div>
          <div className="row-wrap">
            <PlayButton
              src={clip.src}
              tag={`meet:${item.id}:${voice}`}
              label="Listen again"
              ariaLabel={`Listen again, ${VOICE_LABEL[voice].toLowerCase()}'s voice`}
              from={range.from}
              to={range.to}
              onResult={onListened}
            />
            <PlayButton src={clip.src} tag={`meet:${item.id}:${voice}:slow`} label="Slowly" rate={0.75} from={range.from} to={range.to} onResult={onListened} />
          </div>
          {phase === 'say' && <p className="muted small">Syllables in colour carry the stress.</p>}
        </div>
      )}

      {showGreek && phase === 'say' && noMic && (
        <div className="stack-lg reveal">
          <NoMicPanel title="Say it aloud, two or three times" body="Copy the rhythm and the stressed syllables. Speaking out loud is what makes it stick." access={access} onRetry={retryMic} />
        </div>
      )}

      {showGreek && recording && (phase === 'understood' || phase === 'listen' || phase === 'say') && (
        <div className="stack reveal">
          <p className="small muted">{phase === 'understood' ? 'Model, then you:' : 'Your guess, next to the model:'}</p>
          <Compare
            clip={clip}
            models={voices.map((v) => clipFor(item, v))}
            voiceName={VOICE_LABEL[voice]}
            recording={recording}
            autoPlay={autoPlay}
            id={`meet:${item.id}`}
            action={
              phase === 'understood' && !quiet && !noMic ? (
                <RecordControl inline label="Record again" onResult={keep} onProblem={onMicProblem} />
              ) : undefined
            }
          />
        </div>
      )}

      {phase === 'understood' && (
        <div className="stack-lg reveal">
          <WhyNote item={item} frame={frame} open />
          {quiet && item.role === 'say' && (
            <p className="small muted">Listening only today: you'll say this one aloud in your next session.</p>
          )}
          {!say && !quiet && !noMic && !recording && (
            <details className="panel-quiet">
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Repeat it aloud (optional)</summary>
              <div style={{ marginTop: 12 }}>
                <RecordControl label="Record yourself" compact onResult={keep} onProblem={onMicProblem} />
              </div>
            </details>
          )}
        </div>
      )}

      <div className="actions">
        {phase === 'listen' && listens === 0 && (
          <div className="listen-first reveal">
            <PlayButton
              src={clip.src}
              tag={`meet:${item.id}:${voice}`}
              label="Listen"
              ariaLabel={`Listen, ${VOICE_LABEL[voice].toLowerCase()}'s voice`}
              from={range.from}
              to={range.to}
              variant="big"
              onResult={onListened}
            />
            <p className="section-title">Listen first</p>
          </div>
        )}
        {/* The microphone sits in the thumb's reach, above the way out. */}
        {phase === 'try' && !noMic && (
          <>
            <RecordControl label="Record your guess" onResult={(r) => { setRecording(r); setPhase('listen'); }} onProblem={onMicProblem} />
            <MicNote access={access} />
          </>
        )}
        {phase === 'say' && !noMic && (
          <>
            <RecordControl label="Now say it aloud" onResult={(r) => { keep(r); setPhase('understood'); }} onProblem={onMicProblem} />
            <MicNote access={access} />
          </>
        )}
        {phase === 'try' && (
          <button type="button" className="btn btn-secondary btn-block btn-lg" onClick={() => setPhase('listen')}>
            <Eye size={20} aria-hidden="true" /> Show me
          </button>
        )}
        {phase === 'say' && noMic && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={() => setPhase('understood')}>
            I said it
          </button>
        )}
        {phase === 'understood' && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={next} disabled={busy}>
            Next <ArrowRight size={20} aria-hidden="true" />
          </button>
        )}
        {(phase === 'listen' || (phase === 'say' && !noMic)) && (
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setPhase(phase === 'listen' && say ? 'say' : 'understood')}>
            {phase === 'listen' ? 'Skip listening' : "I can't speak right now"}
          </button>
        )}
      </div>
    </div>
  );
}
